/**
 * Theme colours for canvas-drawn widgets (Chart.js).
 *
 * A canvas cannot read CSS variables, and Chart.js cannot parse `oklch()` /
 * `color-mix()`, which is what DaisyUI v5 themes are made of. So the live
 * theme is read from the element's computed style (it inherits from the page,
 * including a nested `data-theme`), every colour is converted to `#rrggbb`,
 * and palettes are built from those. The hex table below is only the fallback
 * for a page that defines no theme variables at all.
 *
 * Pure parts (parse, mix, dedupe, palette building, token resolution) take
 * their inputs as arguments and are unit-tested in node; the browser glue is
 * `getThemeColors(el)` and the shared theme-change watcher.
 */

export const THEME_TOKENS = ['primary', 'secondary', 'accent', 'info', 'success', 'warning', 'error', 'neutral'];
// Every other DaisyUI colour an author can name. Resolved like the semantic
// ones; a name outside both lists never reaches the canvas as a bare word
// (Chart.js can't draw `ghost` or `base-300` — the arc came out black).
const SURFACE_TOKENS = [
  'base-100', 'base-200', 'base-300', 'base-content',
  'primary-content', 'secondary-content', 'accent-content', 'neutral-content',
  'info-content', 'success-content', 'warning-content', 'error-content',
];

// DaisyUI names with no colour of their own, mapped to what they mean in a chart.
const ALIASES = { ghost: 'muted', muted: 'muted' };

// A fill this close to the page's text or background colour reads as black
// (or vanishes): light themes' `neutral` is near-black, so "Not run" became a
// black slice. Such fills are pulled toward the middle.
export const SOFTEN_DISTANCE = 110;

// Approximate DaisyUI defaults, used only when the page has no theme vars.
export const FALLBACK_HEX = {
  primary:        '#4f46e5',
  secondary:      '#f000b8',
  accent:         '#37cdbe',
  info:           '#0ea5e9',
  success:        '#16a34a',
  warning:        '#f59e0b',
  error:          '#dc2626',
  neutral:        '#737373',
  'base-100':     '#ffffff',
  'base-200':     '#f2f2f2',
  'base-300':     '#e5e6e6',
  'base-content': '#1f2937',
  'primary-content':   '#ffffff',
  'secondary-content': '#ffffff',
  'accent-content':    '#1f2937',
  'neutral-content':   '#ffffff',
  'info-content':      '#ffffff',
  'success-content':   '#ffffff',
  'warning-content':   '#1f2937',
  'error-content':     '#ffffff',
};

// DaisyUI v4 kept bare oklch components in short vars: `oklch(var(--p))`.
const V4_VARS = {
  primary: '--p', secondary: '--s', accent: '--a', neutral: '--n',
  info: '--in', success: '--su', warning: '--wa', error: '--er',
  'base-100': '--b1', 'base-content': '--bc',
};

// ── Pure colour maths ──

/** '#rgb' | '#rrggbb' | '#rrggbbaa' | 'rgb(…)' | 'rgba(…)' → {r,g,b,a} or null. */
export function parseRgb(value) {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim().toLowerCase();
  let m = s.match(/^#([0-9a-f]{3,4})$/);
  if (m) {
    const [r, g, b, a = 'f'] = m[1].split('');
    return { r: parseInt(r + r, 16), g: parseInt(g + g, 16), b: parseInt(b + b, 16), a: parseInt(a + a, 16) / 255 };
  }
  m = s.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/);
  if (m) {
    const n = parseInt(m[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: m[2] ? parseInt(m[2], 16) / 255 : 1 };
  }
  m = s.match(/^rgba?\(\s*([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const ch = (p) => p.endsWith('%') ? parseFloat(p) * 2.55 : parseFloat(p);
    const [r, g, b] = parts.slice(0, 3).map(ch);
    let a = parts[3] == null ? 1 : (parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]));
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r: clamp255(r), g: clamp255(g), b: clamp255(b), a: Math.max(0, Math.min(1, a)) };
  }
  return null;
}

function clamp255(n) { return Math.max(0, Math.min(255, Math.round(n))); }

export function toHex({ r, g, b }) {
  return '#' + [r, g, b].map(n => clamp255(n).toString(16).padStart(2, '0')).join('');
}

/** Opaque → '#rrggbb'; translucent → 'rgba(…)'. */
export function formatRgb(c) {
  if (!c) return null;
  return c.a == null || c.a >= 1 ? toHex(c) : `rgba(${clamp255(c.r)},${clamp255(c.g)},${clamp255(c.b)},${+c.a.toFixed(3)})`;
}

/** Mix two hex colours in sRGB; t = share of `b` (0 → a, 1 → b). */
export function mixHex(a, b, t) {
  const ca = parseRgb(a), cb = parseRgb(b);
  if (!ca) return b;
  if (!cb) return a;
  return toHex({ r: ca.r + (cb.r - ca.r) * t, g: ca.g + (cb.g - ca.g) * t, b: ca.b + (cb.b - ca.b) * t });
}

/** Perceptual-ish RGB distance ("redmean"); 0 … ~765. */
export function colorDistance(a, b) {
  const ca = parseRgb(a), cb = parseRgb(b);
  if (!ca || !cb) return Infinity;
  const rm = (ca.r + cb.r) / 2;
  const dr = ca.r - cb.r, dg = ca.g - cb.g, db = ca.b - cb.b;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}

export const DEDUPE_DISTANCE = 40;

/** Drop colours that are near-identical to one already kept (first wins). */
export function dedupeColors(colors, threshold = DEDUPE_DISTANCE) {
  const out = [];
  for (const c of colors) {
    if (!c) continue;
    if (out.every(k => colorDistance(k, c) >= threshold)) out.push(c);
  }
  return out;
}

/** `rgba(…, alpha)` from any hex/rgb colour; anything unparseable passes through. */
export function colorWithAlpha(color, alpha) {
  if (!color || typeof color !== 'string') return alpha === 0 ? 'transparent' : color;
  if (alpha === 0) return 'transparent';
  const c = parseRgb(color);
  if (!c) return color;
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}

// ── Theme resolution (pure, injectable) ──

/**
 * Resolve the theme to hex values.
 *   readVar(name) → the raw custom-property value ('' when unset)
 *   convert(css)  → {r,g,b,a} for any CSS colour (may resolve var()), or null
 * Missing or unconvertible tokens fall back to FALLBACK_HEX; `fromTheme`
 * says whether anything came from the page.
 */
export function resolveThemeColors(readVar, convert) {
  const theme = {};
  let fromTheme = false;
  for (const token of [...THEME_TOKENS, ...SURFACE_TOKENS]) {
    let hex = null;
    const v5 = safe(() => readVar('--color-' + token));
    if (v5) hex = toHexOrNull(parseRgb(v5) || safe(() => convert(v5, '--color-' + token)));
    if (!hex) {
      const v4 = safe(() => readVar(V4_VARS[token]));
      if (v4) hex = toHexOrNull(safe(() => convert(`oklch(${v4})`, V4_VARS[token])));
    }
    if (hex && THEME_TOKENS.includes(token)) fromTheme = true;
    theme[token] = hex || FALLBACK_HEX[token];
  }
  // A quiet grey of this theme: halfway between its text and its background.
  theme.muted = mixHex(theme['base-content'], theme['base-100'], 0.55);
  theme.fromTheme = fromTheme;
  return theme;
}

/**
 * A theme colour as a chart FILL: one that sits on top of the page's text
 * colour (light themes' near-black `neutral`) or its background is pulled
 * toward the middle so a slice or bar never reads as black or disappears.
 * Author-written colours never come through here.
 */
export function chartFill(color, theme) {
  if (!color || !theme) return color;
  const fg = theme['base-content'], bg = theme['base-100'];
  if (colorDistance(color, fg) < SOFTEN_DISTANCE) return mixHex(color, bg, 0.45);
  if (colorDistance(color, bg) < SOFTEN_DISTANCE / 2) return mixHex(color, fg, 0.3);
  return color;
}

function toHexOrNull(c) { return c ? toHex(c) : null; }
function safe(fn) { try { return fn(); } catch { return null; } }

/** Palettes built from a resolved theme. */
export function buildPalettes(theme) {
  const t = theme || FALLBACK_HEX;
  const p = t.primary, bg = t['base-100'], fg = t['base-content'];

  // Categorical: every semantic colour once. A theme whose colours collapse
  // (monochrome themes) is extended with tints/shades so slices still differ.
  let categorical = dedupeColors(THEME_TOKENS.map(k => chartFill(t[k], t)));
  if (categorical.length < 6) {
    const extra = [];
    for (const c of categorical.slice()) {
      extra.push(mixHex(c, bg, 0.4), mixHex(c, fg, 0.4), mixHex(c, '#ffffff', 0.6), mixHex(c, '#000000', 0.5), mixHex(c, bg, 0.7));
    }
    categorical = dedupeColors([...categorical, ...extra], DEDUPE_DISTANCE / 2);
  }

  // Primary: the primary colour, then fading toward the page background.
  const primary = [0, 0.2, 0.38, 0.54, 0.68, 0.8].map(s => mixHex(p, bg, s));

  // Sequential: emphasis (primary pulled toward content) → recessive
  // (primary washed into the background). Light themes: dark → light.
  const sequential = [
    ...[0.45, 0.3, 0.15].map(s => mixHex(p, fg, s)),
    p,
    ...[0.25, 0.45, 0.65, 0.8].map(s => mixHex(p, bg, s)),
  ];

  return { categorical, rainbow: categorical, primary, sequential };
}

const CSS_COLOR_FN = /^(rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|color-mix|light-dark)\(/i;

/**
 * Any colour reference a chart author can write → a Chart.js-safe colour.
 *   'primary' / 'base-content' → live theme value
 *   '#hex' / 'rgb(…)'          → unchanged (the author's choice wins)
 *   'oklch(…)', 'var(--x)', 'red' … → converted when a converter is given
 * Returns null for nothing usable.
 */
export function resolveColorToken(value, theme, convert) {
  if (value == null || value === '') return null;
  const v = String(value).trim();
  const raw = v.toLowerCase().replace(/^--color-/, '');
  const key = ALIASES[raw] || raw;
  if (key === 'muted') return (theme && theme.muted) || mixHex(FALLBACK_HEX['base-content'], FALLBACK_HEX['base-100'], 0.55);
  if (THEME_TOKENS.includes(key)) return chartFill((theme && theme[key]) || FALLBACK_HEX[key], theme);
  if (SURFACE_TOKENS.includes(key)) return (theme && theme[key]) || FALLBACK_HEX[key];
  if (parseRgb(v)) return v;
  if (convert && (CSS_COLOR_FN.test(v) || /^var\(/i.test(v) || /^[a-z]+$/i.test(v))) {
    const c = safe(() => convert(v));
    if (c) return formatRgb(c);
    return null; // the browser rejected it — the caller's fallback, never a black arc
  }
  return !convert && /^[a-z]+$/i.test(v) ? v : null; // no browser to ask: trust a plain CSS name
}

// ── Browser glue ──

let canvasCtx = null;
const convertCache = new Map();

/** CSS colour → {r,g,b,a} via a 1×1 canvas. Returns null when unparseable. */
export function canvasColorToRgb(value) {
  const direct = parseRgb(value);
  if (direct) return direct;
  if (convertCache.has(value)) return convertCache.get(value);
  if (typeof document === 'undefined') return null;
  if (!canvasCtx) {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    canvasCtx = c.getContext('2d', { willReadFrequently: true });
    if (!canvasCtx) return null;
  }
  const ctx = canvasCtx;
  // An invalid fillStyle is silently ignored, so set it over two different
  // sentinels: if the results differ, the value was rejected.
  ctx.fillStyle = '#010203'; ctx.fillStyle = value; const a = ctx.fillStyle;
  ctx.fillStyle = '#030201'; ctx.fillStyle = value; const b = ctx.fillStyle;
  let out = null;
  if (a === b) {
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillRect(0, 0, 1, 1);
    const d = ctx.getImageData(0, 0, 1, 1).data;
    out = { r: d[0], g: d[1], b: d[2], a: d[3] / 255 };
  }
  if (convertCache.size > 500) convertCache.clear();
  convertCache.set(value, out);
  return out;
}

/** A converter scoped to `el`: var() and color-mix() resolve through a probe inside it. */
export function makeElementConverter(el) {
  return (value) => {
    if (!/var\(/i.test(value)) {
      const c = canvasColorToRgb(value);
      if (c) return c;
    }
    if (!el || typeof getComputedStyle === 'undefined' || !el.appendChild) return null;
    const probe = document.createElement('span');
    probe.style.cssText = 'display:none';
    probe.style.color = value;
    if (!probe.style.color) return null; // the browser rejected it
    el.appendChild(probe);
    try {
      const computed = getComputedStyle(probe).color;
      return computed ? canvasColorToRgb(computed) : null;
    } finally {
      probe.remove();
    }
  };
}

const themeCache = new Map();

function themeKey(el) {
  const root = document.documentElement;
  const scoped = el && el.closest ? el.closest('[data-theme]') : null;
  const dark = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)').matches : false;
  return [root.getAttribute('data-theme'), root.className, scoped && scoped.getAttribute('data-theme'), dark].join('|');
}

/** Live theme colours for `el` (cached per theme until the theme changes). */
export function getThemeColors(el) {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') {
    return { ...FALLBACK_HEX, fromTheme: false };
  }
  const key = themeKey(el);
  if (themeCache.has(key)) return themeCache.get(key);
  const target = el && el.isConnected ? el : document.documentElement;
  const cs = getComputedStyle(target);
  const theme = resolveThemeColors((name) => cs.getPropertyValue(name).trim(), makeElementConverter(target));
  if (themeCache.size > 20) themeCache.clear();
  themeCache.set(key, theme);
  return theme;
}

export function clearThemeCache() { themeCache.clear(); convertCache.clear(); }

// ── Theme-change watcher: one observer for every subscriber ──

/**
 * createThemeWatcher({ root, MutationObserver, matchMedia }) — injectable for
 * tests. subscribe(cb) → unsubscribe. The observer and media listener exist
 * only while someone is subscribed.
 */
export function createThemeWatcher(env = {}) {
  const subscribers = new Set();
  let observer = null, mql = null;
  const notify = () => {
    clearThemeCache();
    for (const cb of Array.from(subscribers)) { try { cb(); } catch (e) { console.error('[themeColors]', e); } }
  };
  const start = () => {
    const root = env.root || (typeof document !== 'undefined' ? document.documentElement : null);
    const MO = env.MutationObserver || (typeof MutationObserver !== 'undefined' ? MutationObserver : null);
    if (root && MO) {
      observer = new MO(notify);
      observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    }
    const mm = env.matchMedia || (typeof matchMedia === 'function' ? matchMedia : null);
    if (mm) {
      mql = mm('(prefers-color-scheme: dark)');
      if (mql && mql.addEventListener) mql.addEventListener('change', notify);
    }
  };
  const stop = () => {
    if (observer) { observer.disconnect(); observer = null; }
    if (mql && mql.removeEventListener) mql.removeEventListener('change', notify);
    mql = null;
  };
  return {
    subscribe(cb) {
      subscribers.add(cb);
      if (subscribers.size === 1) start();
      return () => {
        if (!subscribers.delete(cb)) return;
        if (subscribers.size === 0) stop();
      };
    },
    get size() { return subscribers.size; },
    notify,
  };
}

let sharedWatcher = null;
/** Subscribe to page theme changes; returns an unsubscribe function. */
export function onThemeChange(cb) {
  if (!sharedWatcher) sharedWatcher = createThemeWatcher();
  return sharedWatcher.subscribe(cb);
}

/** Stable fingerprint of a resolved theme, to skip re-renders on no-op changes. */
export function themeSignature(theme) {
  return [...THEME_TOKENS, ...SURFACE_TOKENS].map(k => theme && theme[k]).join(',');
}
